import { Form, Head } from "@inertiajs/react"
import { ChevronsUpDown } from "lucide-react"

import InputError from "@/components/input-error"
import GoogleIcon from "@/components/icons/google-icon"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { newIdentityPasswordResetPath, signInPath, signUpPath } from "@/routes"

export default function Login() {
  return (
    <AuthLayout
      title="Log in to your account"
      description="Click the button below to log in with Google"
    >
      <Head title="Log in" />
      <div className="flex flex-col gap-6">
        <form action="/auth/google_oauth2" method="post">
          <input
            type="hidden"
            name="authenticity_token"
            value={
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content") || ""
            }
          />
          <Button variant="outline" type="submit" className="w-full">
            <GoogleIcon className="mr-2 h-4 w-4" />
            Sign in with Google
          </Button>
        </form>

        <Collapsible>
          <CollapsibleTrigger asChild>
            <div className="flex w-full items-center justify-center">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground gap-2"
              >
                more options coming soon...
                <ChevronsUpDown className="h-4 w-4" />
                <span className="sr-only">Toggle email login</span>
              </Button>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-6">
            <div className="relative mt-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background text-muted-foreground px-2">
                  OR CONTINUE WITH EMAIL (DISABLED)
                </span>
              </div>
            </div>

            <Form
              method="post"
              action={signInPath()}
              resetOnSuccess={["password"]}
              className="flex flex-col gap-6"
            >
              {({ processing, errors }) => (
                <>
                  <div className="grid gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        disabled
                        autoFocus
                        tabIndex={1}
                        autoComplete="email"
                        placeholder="email@example.com"
                      />
                      <InputError message={errors.email} />
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center">
                        <Label htmlFor="password">Password</Label>
                        <TextLink
                          href={newIdentityPasswordResetPath()}
                          className="ml-auto text-sm"
                          tabIndex={5}
                        >
                          Forgot password?
                        </TextLink>
                      </div>
                      <Input
                        id="password"
                        type="password"
                        name="password"
                        required
                        disabled
                        tabIndex={2}
                        autoComplete="current-password"
                        placeholder="Password"
                      />
                      <InputError message={errors.password} />
                    </div>

                    <Button
                      type="submit"
                      className="mt-4 w-full"
                      tabIndex={4}
                      disabled
                    >
                      {processing && <Spinner />}
                      Log in
                    </Button>
                  </div>

                  <div className="text-muted-foreground text-center text-sm">
                    Don&apos;t have an account?{" "}
                    <TextLink href={signUpPath()} tabIndex={5}>
                      Sign up
                    </TextLink>
                  </div>
                </>
              )}
            </Form>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </AuthLayout>
  )
}
